import type { TaskListColor, TaskListOutput } from "@orsane/schema/task-lists";
import { useNavigate } from "@tanstack/react-router";
import { Archive, Ellipsis, Files, Link, LogOut, Trash2 } from "lucide-react";
import { Suspense, useRef, useState } from "react";
import { toast } from "sonner";
import { useTaskListFetchingStore } from "@/common/stores";
import ColorSelector from "@/components/ColorSelector";
import DuplicateListDialog from "@/components/DuplicateListDialog";
import InvisibleAutogrowInput from "@/components/InvisbleAutogrowInput";
import { StackedAvatars } from "@/components/StackedAvatars";
import TaskFilter from "@/components/task-filter/TaskFilter";
import DisplayOptionsPopover from "@/components/task-table/DisplayOptionsPopover";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    useArchiveTaskListMutation,
    useDeleteTaskListMutation,
    useLeaveTaskListMutation,
    useUpdateTaskListColorMutation,
    useUpdateTaskListNameMutation,
} from "@/mutations/task-list-mutations";
import { useSuspenseTaskListUsers, useTaskListUsers } from "@/queries/list-queries";
import { useSessionQuery } from "@/queries/queries";
import { inboxRoute } from "@/routes/inboxRoute";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "./DefaultAlertDialog";
import LoadingSpinner from "./loading-spinner";
import SharePopover from "./SharePopover";

function TaskListUsers({ taskListId }: { taskListId: number }) {
    const { data: taskListUsers } = useSuspenseTaskListUsers(taskListId);
    if (!taskListUsers || taskListUsers.length === 0) {
        return null;
    }
    return <StackedAvatars users={taskListUsers} size={28} overlap={10} tooltipSuffix="has access to this list" />;
}

export default function TaskListHeader({ taskList }: { taskList: TaskListOutput }) {
    const { mutate: updateTaskListName } = useUpdateTaskListNameMutation();
    const { mutate: archiveTaskList } = useArchiveTaskListMutation();
    const { mutateAsync: deleteTaskList } = useDeleteTaskListMutation();
    const { mutateAsync: leaveTaskList } = useLeaveTaskListMutation();
    const { mutate: updateTaskListColor } = useUpdateTaskListColorMutation();
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const archiveAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const unArchiveAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const leaveAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const navigate = useNavigate();
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const { fetching } = useTaskListFetchingStore();
    const { data: sessionData } = useSessionQuery();
    const { data: taskListUsers } = useTaskListUsers(taskList.id);

    // The last member cannot leave: a list with no members is invisible to
    // everyone in the org while its tasks stay behind. The server enforces this
    // too — this only keeps the menu item from looking available.
    const isOnlyMember = taskListUsers !== undefined && taskListUsers.length <= 1;

    const handleTaskListNameChange = (value: string) => {
        updateTaskListName({ id: taskList.id, name: value });
    };

    const handleTaskListColorChange = (color: TaskListColor) => {
        updateTaskListColor({ id: taskList.id, color });
    };

    const handleArchiveList = async () => {
        const confirmed = await archiveAlertDialogRef.current?.show();
        if (confirmed) {
            archiveTaskList({ id: taskList.id, isArchived: true });
        }
    };

    const handleUnarchiveList = async () => {
        const confirmed = await unArchiveAlertDialogRef.current?.show();
        if (confirmed) {
            archiveTaskList({ id: taskList.id, isArchived: false });
        }
    };

    const handleCopyListLink = () => {
        const url = `${window.location.origin}/lists/${taskList.id}`;
        navigator.clipboard.writeText(url);
        toast.success("Link copied", {
            description: `The link to this list has been copied to your clipboard.`,
        });
    };

    const handleDuplicateList = () => {
        setDuplicateDialogOpen(true);
    };

    const handleLeaveList = async () => {
        const confirmed = await leaveAlertDialogRef.current?.show();
        if (!confirmed) {
            return;
        }
        try {
            await leaveTaskList({ id: taskList.id });
        } catch (error) {
            toast.error("Could not leave list", {
                description: error instanceof Error ? error.message : "Please try again.",
            });
            return;
        }
        toast.success("You left the list", {
            description: `'${taskList.name}' has been removed from your sidebar. Tasks assigned to you stay assigned.`,
        });
        navigate({ to: inboxRoute.to });
    };

    const handleDeleteList = async () => {
        const confirmed = await deleteAlertDialogRef.current?.show();
        if (confirmed) {
            await deleteTaskList({ id: taskList.id });
            toast.success("List deleted", {
                description: `'${taskList.name}' has been deleted.`,
            });
            setTimeout(() => {
                navigate({ to: inboxRoute.to });
            });
        }
    };

    return (
        <>
            <div
                data-slot="header"
                className="px-6 py-2 pl-3 text-zinc-600 flex items-center gap-2 border-b justify-between"
            >
                <div className="flex items-center">
                    <ColorSelector
                        key={taskList.id}
                        disabled={taskList.isArchived}
                        color={taskList.color}
                        onApply={handleTaskListColorChange}
                    />
                    {!taskList.isArchived && (
                        <InvisibleAutogrowInput value={taskList.name} onChange={handleTaskListNameChange} />
                    )}
                    {taskList.isArchived && <span className="text-zinc-500 pl-1.5">{taskList.name} [archived]</span>}
                    {fetching && <LoadingSpinner />}
                </div>
                <div className="ml-auto">
                    <Suspense fallback={null}>
                        <TaskListUsers taskListId={taskList.id} />
                    </Suspense>
                </div>
                <div>
                    <SharePopover taskList={taskList} />
                </div>
                <div>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button variant="outline" size="sm">
                                    <Ellipsis className="w-4 h-4" />
                                </Button>
                            }
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent side="bottom" align="end">
                            <DropdownMenuItem onClick={handleDuplicateList}>
                                <Files />
                                Duplicate list
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCopyListLink}>
                                <Link />
                                Copy list link
                            </DropdownMenuItem>
                            {/* <DropdownMenuItem>
                                <Download />
                                Export as Excel
                            </DropdownMenuItem> */}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLeaveList} disabled={isOnlyMember}>
                                <LogOut /> Leave list
                            </DropdownMenuItem>
                            {!taskList.isArchived && (
                                <DropdownMenuItem
                                    onClick={handleArchiveList}
                                    disabled={taskList.createdById !== sessionData?.user.id}
                                >
                                    <Archive /> Archive list
                                </DropdownMenuItem>
                            )}
                            {!!taskList.isArchived && (
                                <DropdownMenuItem
                                    onClick={handleUnarchiveList}
                                    disabled={taskList.createdById !== sessionData?.user.id}
                                >
                                    <Archive /> Unarchive list
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={handleDeleteList}
                                disabled={taskList.isArchived || taskList.createdById !== sessionData?.user.id}
                            >
                                <Trash2 /> Delete list
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <div
                data-slot="filter"
                className="pl-3.5 pr-4 py-1  text-zinc-500 flex items-center gap-2 border-b justify-between"
            >
                <div className="mr-auto">
                    <TaskFilter />
                </div>
                <div className="flex items-center gap-2">
                    <DisplayOptionsPopover disabled={taskList.isArchived} />
                </div>
            </div>
            <DuplicateListDialog
                key={taskList.id}
                taskList={taskList}
                open={duplicateDialogOpen}
                onOpenChange={setDuplicateDialogOpen}
            />
            <DefaultAlertDialog
                ref={deleteAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Delete this list?"
                description={`'${taskList.name}' will be deleted permanently. This action cannot be undone.`}
            />
            <DefaultAlertDialog
                ref={archiveAlertDialogRef}
                type={AlertDialogType.Archive}
                title="Archive this list?"
                description={`'${taskList.name}' will no longer appear in anyone's sidebar. You won't be able to add new tasks, but existing tasks will be preserved and searchable. This action can be undone anytime.`}
            />
            <DefaultAlertDialog
                ref={leaveAlertDialogRef}
                type={AlertDialogType.Leave}
                title="Leave this list?"
                description={`'${taskList.name}' will disappear from your sidebar and you will lose access to its tasks. Tasks assigned to you stay assigned and stay visible. Someone with access can add you back.`}
            />
            <DefaultAlertDialog
                ref={unArchiveAlertDialogRef}
                type={AlertDialogType.Unarchive}
                title="Unarchive this list?"
                description={`'${taskList.name}' will appear in the sidebar of users with access. External users will be able to view the list again.`}
            />
        </>
    );
}
